/* VastraGharr Luxe 3.0 — collection & search pages
   Enhances the markup of sections/main-collection-product-grid.liquid, sections/main-search.liquid and
   snippets/facets.liquid on top of Dawn's assets/facets.js (FacetFiltersForm), which it never replaces:
   - grid density toggle (remembered), desktop filter sidebar toggle, sticky toolbar offset
   - instant sort; on phones a sort bottom sheet; the filter drawer is a bottom sheet with "Show N results"
   - quick filter chips and "Under Rs. X" price presets
   - every re-render is built from the filter forms themselves, so a filter ticked a moment before a sort
     change or a chip tap (still inside Dawn's 800ms debounce) is kept, and Dawn's late debounced submit
     for the same state is skipped instead of rendering (and pushing a history entry) twice
   - "Load more" through the Section Rendering API, optional infinite scroll, and back-button restore of the
     loaded pages + scroll position (sessionStorage, per URL, 30 minutes)
   - empty states: recently viewed cards, recent searches; records successful searches (vg:searches)
   Theme editor safe: per-root listeners are removed on shopify:section:unload. */
(() => {
  if (window.VG && window.VG.collectionReady) return;
  window.VG = window.VG || {};
  window.VG.collectionReady = true;

  const KEYS = {
    d: 'vg:grid-d',
    m: 'vg:grid-m',
    filters: 'vg:filters-hidden',
    wishlist: 'vg:wishlist',
    recent: 'vg:recent',
    searches: 'vg:searches',
  };
  const RESTORE_PREFIX = 'vg:lm:';
  const RESTORE_TTL = 30 * 60 * 1000;
  const RESTORE_MAX_PAGES = 10;
  const AUTO_LOAD_LIMIT = 4;
  const SEARCH_LIMIT = 6;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const closest = (node, selector) => (node && node.closest ? node.closest(selector) : null);

  const store = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* storage unavailable (private mode) — preference just isn't remembered */
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
    list(key) {
      try {
        const value = JSON.parse(this.get(key) || '[]');
        return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item) : [];
      } catch (error) {
        return [];
      }
    },
  };

  const session = {
    get(key) {
      try {
        return JSON.parse(window.sessionStorage.getItem(key) || 'null');
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* ignore */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
  };

  const roots = new Map(); // root element -> { controller, infinite }
  const hasFacets = () => typeof FacetFiltersForm !== 'undefined' && !!document.getElementById('ProductGridContainer');
  const getRoot = (element) => closest(element, '[data-vg-collection]');
  const isRendered = (element) => !!(element && element.getClientRects().length);

  function currentParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    return params;
  }

  function plural(count, one, many) {
    return count === 1 ? one : many;
  }

  /* ---------- Grid density ---------- */
  function computeSizes(root) {
    const d = Number(root.dataset.colsD) || 3;
    const m = Number(root.dataset.colsM) || 2;
    const pageWidth = Number(root.dataset.pageWidth) || 1200;
    const hasSidebar = root.classList.contains('vg-collection--sidebar');
    const sidebarVisible = hasSidebar && !root.classList.contains('vg-collection--filters-hidden');
    const offset = sidebarVisible ? Number(root.dataset.offsetRem) || 40 : 10;
    let t = sidebarVisible ? 2 : 3;
    if (d < 3) t = d;
    const max = Math.round((pageWidth - offset * 10) / d);
    return (
      `(min-width: ${pageWidth}px) ${max}px, ` +
      `(min-width: 990px) calc((100vw - ${offset}rem) / ${d}), ` +
      `(min-width: 750px) calc((100vw - ${offset}rem) / ${t}), ` +
      `calc((100vw - 3rem) / ${m})`
    );
  }

  function applySizes(root) {
    const sizes = computeSizes(root);
    root.querySelectorAll('[data-vg-grid] .vg-card__img').forEach((img) => {
      if (img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
    });
  }

  function flashGrid(root) {
    const grid = root.querySelector('[data-vg-grid]');
    if (!grid || reduceMotion.matches || typeof grid.animate !== 'function') return;
    grid.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)' });
  }

  function setCols(root, axis, value, persist) {
    const attr = axis === 'd' ? 'data-cols-d' : 'data-cols-m';
    const allowed = axis === 'd' ? ['2', '3', '4'] : ['1', '2'];
    const next = String(value);
    if (!allowed.includes(next)) return;
    root.setAttribute(attr, next);
    root.querySelectorAll(`[data-vg-cols-${axis}]`).forEach((button) => {
      button.setAttribute('aria-pressed', String(button.getAttribute(`data-vg-cols-${axis}`) === next));
    });
    if (persist) store.set(KEYS[axis], next);
    applySizes(root);
  }

  /* ---------- Desktop sidebar toggle ---------- */
  function setSidebar(root, hidden, persist) {
    if (!root.classList.contains('vg-collection--sidebar')) return;
    root.classList.toggle('vg-collection--filters-hidden', hidden);
    root.querySelectorAll('[data-vg-sidebar-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', String(!hidden));
      const label = button.querySelector('[data-vg-sidebar-label]');
      if (label) label.textContent = hidden ? label.dataset.labelShow : label.dataset.labelHide;
    });
    if (persist) {
      if (hidden) store.set(KEYS.filters, '1');
      else store.remove(KEYS.filters);
    }
    applySizes(root);
  }

  function mirrorFilterCount(root) {
    const source = root.querySelector('.mobile-facets__open .vg-filter-count');
    root.querySelectorAll('[data-vg-sidebar-toggle]').forEach((button) => {
      let badge = button.querySelector('.vg-filter-count');
      if (source) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'vg-filter-count';
          badge.setAttribute('aria-hidden', 'true');
          button.appendChild(badge);
        }
        badge.textContent = source.textContent.trim();
      } else if (badge) {
        badge.remove();
      }
    });
  }

  /* ---------- Sticky toolbar ---------- */
  function initSticky(root, signal) {
    const toolbar = root.querySelector('[data-vg-toolbar]');
    if (!toolbar) return;

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        root.style.setProperty('--vg-toolbar-h', `${Math.round(toolbar.offsetHeight)}px`);
      });
      observer.observe(toolbar);
      signal.addEventListener('abort', () => observer.disconnect());
    } else {
      root.style.setProperty('--vg-toolbar-h', `${Math.round(toolbar.offsetHeight)}px`);
    }

    if (!root.classList.contains('vg-collection--sticky')) return;

    let frame = null;
    let trailing = null;
    let lastTop = -1;
    const update = () => {
      frame = null;
      // Reads first, writes after: no forced layout inside the frame.
      const header = document.querySelector('.section-header');
      let top = 0;
      if (header) {
        const position = window.getComputedStyle(header).position;
        if (position === 'sticky' || position === 'fixed') {
          top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }
      }
      const stuck = window.scrollY > 0 && toolbar.getBoundingClientRect().top <= top + 1;
      if (top !== lastTop) {
        lastTop = top;
        root.style.setProperty('--vg-toolbar-top', `${top}px`);
      }
      toolbar.classList.toggle('is-stuck', stuck);
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
      window.clearTimeout(trailing);
      trailing = window.setTimeout(update, 220);
    };

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    signal.addEventListener('abort', () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.clearTimeout(trailing);
    });
    update();
  }

  function scrollToResults(root) {
    const body = root.querySelector('.vg-collection__body');
    if (!body) return;
    const toolbar = root.querySelector('[data-vg-toolbar]');
    const top = parseInt(root.style.getPropertyValue('--vg-toolbar-top'), 10) || 0;
    const offset = top + (toolbar ? toolbar.offsetHeight : 0) + 8;
    const target = body.getBoundingClientRect().top + window.scrollY - offset;
    if (window.scrollY > target + 4) {
      window.scrollTo({ top: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  }

  /* ---------- Filter state ---------- */
  function defaultSort() {
    const select = document.querySelector('[data-vg-sort]');
    return select ? select.dataset.defaultSort || '' : '';
  }

  /* One canonical string per filter state: empty values dropped, prices as plain numbers, default sort ignored */
  function normalize(query) {
    const params = new URLSearchParams(query || '');
    const fallbackSort = defaultSort();
    const entries = [];
    params.forEach((value, key) => {
      if (key === 'page' || key === 'section_id') return;
      let text = String(value).trim();
      if (!text) return;
      if (/^filter\.v\.price\.(gte|lte)$/.test(key)) {
        const amount = parseFloat(text.replace(/[^\d.]/g, ''));
        if (!Number.isFinite(amount)) return;
        text = String(amount);
      }
      if (key === 'sort_by' && fallbackSort && text === fallbackSort) return;
      entries.push(`${key}=${text}`);
    });
    return entries.sort().join('&');
  }

  /* The filter form the shopper is looking at is the source of truth (mirrors facets.js onSubmitHandler) */
  function stateParams() {
    const desktop = document.getElementById('FacetFiltersForm');
    const mobile = document.getElementById('FacetFiltersFormMobile');
    let params;
    if (desktop && isRendered(desktop)) {
      const parts = [];
      document.querySelectorAll('facet-filters-form form').forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          parts.push(new URLSearchParams(new FormData(form)).toString());
        }
      });
      params = new URLSearchParams(parts.filter(Boolean).join('&'));
    } else if (mobile) {
      params = new URLSearchParams(new FormData(mobile));
    } else {
      params = currentParams();
    }
    const toolbarSort = document.querySelector('[data-vg-sort]');
    if (toolbarSort) params.set('sort_by', toolbarSort.value);
    params.delete('page');
    params.delete('section_id');
    return params;
  }

  function setShowResultsLoading(on) {
    document.querySelectorAll('[data-vg-show-results]').forEach((button) => {
      button.classList.toggle('is-loading', on);
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !on);
    });
  }

  let loadingTimer = null;
  function showResultsPending() {
    setShowResultsLoading(true);
    window.clearTimeout(loadingTimer);
    loadingTimer = window.setTimeout(() => setShowResultsLoading(false), 8000);
  }

  /* Skip Dawn renders that would show the state already on screen (e.g. its late debounced submit) */
  function patchFacets() {
    if (typeof FacetFiltersForm === 'undefined' || FacetFiltersForm.vgPatched) return;
    // Dawn's debounced submit reads event.target.closest('form'); when a sort / chip render replaced the filters
    // inside the debounce window that input is detached and its state has already been rendered.
    const originalSubmit = FacetFiltersForm.prototype.onSubmitHandler;
    FacetFiltersForm.prototype.onSubmitHandler = function (event) {
      const target = event && event.target;
      if (!target || !target.isConnected || !target.closest || !target.closest('form')) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        setShowResultsLoading(false);
        return;
      }
      return originalSubmit.call(this, event);
    };
    const originalRenderPage = FacetFiltersForm.renderPage;
    FacetFiltersForm.renderPage = function (searchParams, event, updateURLHash = true) {
      const query = String(searchParams || '');
      if (updateURLHash !== false && normalize(query) === normalize(window.location.search.slice(1))) {
        FacetFiltersForm.toggleActiveFacets(false);
        setShowResultsLoading(false);
        document
          .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
          .forEach((spinner) => spinner.classList.add('hidden'));
        return;
      }
      return originalRenderPage.call(FacetFiltersForm, query, event, updateURLHash);
    };
    FacetFiltersForm.vgPatched = true;
  }

  function renderParams(root, params) {
    const query = params.toString();
    if (hasFacets()) {
      patchFacets();
      FacetFiltersForm.renderPage(query);
      if (root) scrollToResults(root);
    } else {
      window.location.search = query;
    }
  }

  /* ---------- Sort ---------- */
  function updateSortDisplay(select) {
    const field = select.closest('.vg-sort');
    const option = select.options[select.selectedIndex];
    const name = option ? option.textContent.trim() : '';
    if (field) {
      const current = field.querySelector('[data-vg-sort-current]');
      const spoken = field.querySelector('[data-vg-sort-current-sr]');
      if (current) current.textContent = name;
      if (spoken) spoken.textContent = name;
      // Gold underline on the toolbar sort while a non-default order is applied
      if (select.dataset.defaultSort) field.classList.toggle('is-active', select.value !== select.dataset.defaultSort);
    }
  }

  function syncSortSelects(value, except) {
    document.querySelectorAll('select[name="sort_by"]').forEach((other) => {
      if (other !== except && Array.from(other.options).some((option) => option.value === value)) {
        other.value = value;
      }
    });
    document.querySelectorAll('[data-vg-sort-option]').forEach((radio) => {
      radio.checked = radio.value === value;
    });
  }

  function syncSortFromUrl(root) {
    const value = new URLSearchParams(window.location.search).get('sort_by');
    root.querySelectorAll('[data-vg-sort]').forEach((select) => {
      const target = value || select.dataset.defaultSort;
      if (target && select.value !== target && Array.from(select.options).some((option) => option.value === target)) {
        select.value = target;
      }
      updateSortDisplay(select);
      root.querySelectorAll('[data-vg-sort-option]').forEach((radio) => {
        radio.checked = radio.value === select.value;
      });
    });
  }

  function applySort(select) {
    updateSortDisplay(select);
    syncSortSelects(select.value, select);
    const root = getRoot(select);
    const params = stateParams();
    params.set('sort_by', select.value);
    renderParams(root, params);
  }

  document.addEventListener(
    'input',
    (event) => {
      const target = event.target;
      const select = closest(target, '[data-vg-sort]');
      if (select) {
        // Handle the toolbar sort ourselves (instant, keeps every active filter) instead of facets.js' debounced submit.
        event.stopPropagation();
        applySort(select);
        return;
      }
      // Filter sheet: the count on "Show N results" is about to change
      if (closest(target, '#FacetFiltersFormMobile') && !(target.name === 'sort_by')) showResultsPending();
    },
    true
  );

  /* ---------- Sort bottom sheet (phones) ---------- */
  let openSheet = null;
  let sheetTrigger = null;
  let sheetCloseTimer = null;
  let sheetPointer = false;

  function sheetCountText() {
    const count = document.getElementById('ProductCount');
    return count ? count.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function openSortSheet(trigger) {
    const sheet = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!sheet) return;
    if (openSheet) closeSortSheet(false);
    const root = getRoot(trigger);
    const select = root ? root.querySelector('[data-vg-sort]') : null;
    if (select) {
      sheet.querySelectorAll('[data-vg-sort-option]').forEach((radio) => {
        radio.checked = radio.value === select.value;
      });
    }
    const countEl = sheet.querySelector('[data-vg-sheet-count]');
    const countText = sheetCountText();
    if (countEl && countText) countEl.textContent = countText;

    window.clearTimeout(sheetCloseTimer);
    sheetPointer = false;
    sheet.hidden = false;
    // Next frame so the slide-up transition runs from the closed position
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => sheet.classList.add('is-open')));
    const toolbar = sheet.closest('[data-vg-toolbar]');
    if (toolbar) toolbar.classList.add('is-sheet-open');
    document.body.classList.add('overflow-hidden-mobile', 'vg-sheet-open');
    trigger.setAttribute('aria-expanded', 'true');
    openSheet = sheet;
    sheetTrigger = trigger;

    const panel = sheet.querySelector('.vg-sheet__panel');
    const focusTarget = sheet.querySelector('[data-vg-sort-option]:checked') || panel;
    if (typeof window.trapFocus === 'function') window.trapFocus(panel, focusTarget);
    else if (focusTarget) focusTarget.focus();
  }

  function closeSortSheet(returnFocus = true) {
    if (!openSheet) return;
    const sheet = openSheet;
    const trigger = sheetTrigger;
    openSheet = null;
    sheetTrigger = null;
    sheet.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden-mobile', 'vg-sheet-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(returnFocus ? trigger : null);
    else if (returnFocus && trigger) trigger.focus();
    sheetCloseTimer = window.setTimeout(
      () => {
        sheet.hidden = true;
        const toolbar = sheet.closest('[data-vg-toolbar]');
        if (toolbar) toolbar.classList.remove('is-sheet-open');
      },
      reduceMotion.matches ? 0 : 380
    );
  }

  function chooseSort(radio) {
    const root = getRoot(radio);
    const select = root ? root.querySelector('[data-vg-sort]') : null;
    closeSortSheet(true);
    if (!select || select.value === radio.value) return;
    select.value = radio.value;
    applySort(select);
  }

  document.addEventListener('click', (event) => {
    const opener = closest(event.target, '[data-vg-sort-open]');
    if (opener) {
      event.preventDefault();
      openSortSheet(opener);
      return;
    }
    if (openSheet && closest(event.target, '[data-vg-sheet-close]')) {
      event.preventDefault();
      closeSortSheet(true);
      return;
    }
    // Tapping the order that is already applied simply closes the sheet
    const option = closest(event.target, '.vg-sheet__option');
    if (option && openSheet && openSheet.contains(option)) {
      const radio = option.querySelector('[data-vg-sort-option]');
      const root = getRoot(option);
      const select = root ? root.querySelector('[data-vg-sort]') : null;
      if (radio && select && radio.checked && radio.value === select.value && event.target === radio) {
        closeSortSheet(true);
      }
    }
  });

  document.addEventListener('pointerdown', (event) => {
    sheetPointer = !!(openSheet && openSheet.contains(event.target));
  });

  document.addEventListener('change', (event) => {
    const radio = closest(event.target, '[data-vg-sort-option]');
    if (!radio || !radio.checked) return;
    // Touch / mouse picks apply straight away; arrow keys only move the choice (Enter applies)
    if (sheetPointer) chooseSort(radio);
  });

  document.addEventListener('keydown', (event) => {
    if (!openSheet) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSortSheet(true);
      return;
    }
    if (event.key === 'Enter') {
      const radio = closest(event.target, '[data-vg-sort-option]');
      if (radio) {
        event.preventDefault();
        radio.checked = true;
        chooseSort(radio);
      }
    }
  });

  /* ---------- Quick filter chips ---------- */
  document.addEventListener('click', (event) => {
    const chip = closest(event.target, '[data-vg-chip]');
    if (!chip || !hasFacets()) return;
    event.preventDefault();

    const chipParams = new URLSearchParams(chip.dataset.vgChip);
    const params = stateParams();
    // The server renders aria-pressed (it compares amounts). The URL can't be trusted here: after a facets
    // form submit the price arrives money-formatted ("2,499.00"), which never equals the chip's "2499".
    const isActive = chip.getAttribute('aria-pressed') === 'true';

    Array.from(new Set(chipParams.keys())).forEach((key) => params.delete(key));
    if (!isActive) chipParams.forEach((value, key) => params.append(key, value));

    const row = chip.closest('.vg-quick-filters');
    if (row) row.classList.add('is-loading');
    chip.classList.toggle('is-active', !isActive);
    chip.setAttribute('aria-pressed', String(!isActive));
    renderParams(getRoot(chip), params);
  });

  // Chips are links with role="button": make Space activate them like a native button.
  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ' && event.code !== 'Space') return;
    const chip = closest(event.target, '[data-vg-chip]');
    if (!chip) return;
    event.preventDefault();
    chip.click();
  });

  /* ---------- Price presets (snippets/price-facet.liquid) ---------- */
  document.addEventListener('click', (event) => {
    const preset = closest(event.target, '[data-vg-price-max]');
    if (!preset) return;
    const range = preset.closest('price-range');
    if (!range) return;
    event.preventDefault();

    const inputs = range.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;

    const isActive = preset.getAttribute('aria-pressed') === 'true';
    minInput.value = '';
    maxInput.value = isActive ? '' : preset.dataset.vgPriceMax;

    range.querySelectorAll('[data-vg-price-max]').forEach((button) => {
      const on = !isActive && button === preset;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-pressed', String(on));
    });

    maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    maxInput.dispatchEvent(new Event('input', { bubbles: true }));
  });

  /* ---------- Wishlist state for freshly inserted cards ---------- */
  function syncWishlist(scope) {
    if (!scope) return;
    if (window.VG.wishlist && typeof window.VG.wishlist.sync === 'function') {
      window.VG.wishlist.sync(scope);
      return;
    }
    const list = store.list(KEYS.wishlist);
    const strings = (window.VG.config && window.VG.config.strings) || {};
    scope.querySelectorAll('[data-vg-wishlist-toggle]').forEach((button) => {
      const on = list.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-active', on);
      button.setAttribute(
        'aria-label',
        on ? strings.wishlistRemove || 'Remove from wishlist' : strings.wishlistAdd || 'Add to wishlist'
      );
    });
  }

  /* ---------- Load more ---------- */
  function restoreKey() {
    const params = currentParams().toString();
    return `${RESTORE_PREFIX}${window.location.pathname}${params ? `?${params}` : ''}`;
  }

  function startPage(root) {
    const pagination = root.querySelector('[data-vg-pagination]');
    return pagination ? Number(pagination.dataset.page) || 1 : 1;
  }

  function saveRestoreState(root, extra) {
    const start = startPage(root);
    const loaded = Number(root.dataset.vgLoadedPage) || start;
    const key = restoreKey();
    if (loaded <= start) {
      session.remove(key);
      return;
    }
    const state = Object.assign(session.get(key) || {}, { start, page: loaded, ts: Date.now() }, extra || {});
    session.set(key, state);
  }

  function updateLoadStatus(root, finished) {
    const pagination = root.querySelector('[data-vg-pagination]');
    if (!pagination) return;
    const total = Number(pagination.dataset.total) || 0;
    const offset = Number(pagination.dataset.offset) || 0;
    const label = pagination.dataset.label || 'products';
    const count = root.querySelectorAll('[data-vg-grid] > [data-vg-product-item]').length;
    const shown = finished ? total : Math.min(total, offset + count);
    const status = pagination.querySelector('[data-vg-load-status]');
    if (status) {
      status.textContent =
        offset > 0 ? `Showing ${offset + 1}–${shown} of ${total} ${label}` : `Showing ${shown} of ${total} ${label}`;
    }
    const bar = pagination.querySelector('[data-vg-load-bar]');
    if (bar && total > 0) bar.style.width = `${Math.round((shown / total) * 100)}%`;
  }

  async function fetchPage(root, page) {
    const params = currentParams();
    params.set('page', page);
    params.set('section_id', root.dataset.sectionId);
    const response = await fetch(`${window.location.pathname}?${params.toString()}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function setButtonLoading(button, on) {
    if (!button) return;
    const spinner = button.querySelector('.loading__spinner');
    if (on) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /* Card-shaped placeholders added the moment "Load more" is pressed: the layout grows while the tap still counts
     as recent input, and the real cards then take exactly that room (no layout shift when they arrive). */
  function addSkeletons(root) {
    const grid = root.querySelector('[data-vg-grid]');
    const pagination = root.querySelector('[data-vg-pagination]');
    if (!grid || !pagination) return;
    const items = grid.querySelectorAll('[data-vg-product-item]');
    const total = Number(pagination.dataset.total) || 0;
    const offset = Number(pagination.dataset.offset) || 0;
    const size = Number(pagination.dataset.pageSize) || 0;
    const count = Math.min(size, Math.max(0, total - offset - items.length));
    if (!count || !items.length) return;
    const last = items[items.length - 1];
    const height = Math.round(last.getBoundingClientRect().height);
    const media = last.querySelector('.vg-card__media-wrap');
    const ratio = media ? media.style.getPropertyValue('--vg-card-ar') : '';
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i += 1) {
      const item = document.createElement('li');
      item.className = 'grid__item vg-collection__item vg-skeleton';
      item.setAttribute('aria-hidden', 'true');
      if (height > 0) item.style.height = `${height}px`;
      if (ratio) item.style.setProperty('--vg-card-ar', ratio);
      item.innerHTML =
        '<span class="vg-skeleton__card"><span class="vg-skeleton__media"></span><span class="vg-skeleton__line"></span><span class="vg-skeleton__line vg-skeleton__line--short"></span></span>';
      fragment.appendChild(item);
    }
    grid.appendChild(fragment);
  }

  function removeSkeletons(root) {
    root.querySelectorAll('[data-vg-grid] > .vg-skeleton').forEach((item) => item.remove());
  }

  /* Appends the products of a fetched page; returns the new items and whether that was the last page */
  function appendPage(root, doc, page) {
    const grid = root.querySelector('[data-vg-grid]');
    const button = root.querySelector('[data-vg-pagination] [data-vg-load-more]');
    if (!grid) return { added: [], finished: true };
    removeSkeletons(root);
    const incomingGrid = doc.querySelector('[data-vg-grid]');
    const items = incomingGrid ? Array.from(incomingGrid.children).filter((el) => el.hasAttribute('data-vg-product-item')) : [];

    const seen = new Set(
      Array.from(grid.querySelectorAll('[data-vg-product-item] [data-product-handle]')).map((el) => el.dataset.productHandle)
    );
    const fragment = document.createDocumentFragment();
    const added = [];
    items.forEach((item, index) => {
      const card = item.querySelector('[data-product-handle]');
      if (card && seen.has(card.dataset.productHandle)) return;
      item.classList.remove('scroll-trigger', 'animate--slide-in');
      item.removeAttribute('data-vg-reveal');
      item.classList.add('vg-collection__item--appended');
      item.style.setProperty('--vg-append-index', String(Math.min(index, 8)));
      fragment.appendChild(item);
      added.push(item);
    });
    grid.appendChild(fragment);

    const incomingButton = doc.querySelector('[data-vg-pagination] [data-vg-load-more]');
    const finished = !incomingButton;
    if (button && incomingButton) {
      button.dataset.nextPage = incomingButton.dataset.nextPage;
      button.setAttribute('href', incomingButton.getAttribute('href'));
    }

    const fallback = root.querySelector('.vg-pagination-fallback');
    const incomingFallback = doc.querySelector('.vg-pagination-fallback');
    if (fallback && incomingFallback) fallback.innerHTML = incomingFallback.innerHTML;

    root.dataset.vgLoadedPage = String(page);
    updateLoadStatus(root, finished);
    applySizes(root);
    syncWishlist(grid);
    if (finished && button) button.remove();
    document.dispatchEvent(new CustomEvent('vg:collection:append', { detail: { root, items: added } }));
    return { added, finished };
  }

  async function loadMore(root, button, options = {}) {
    const grid = root.querySelector('[data-vg-grid]');
    if (!grid || !button) return;
    const hadFocus = document.activeElement === button;
    const page = Number(button.dataset.nextPage);
    setButtonLoading(button, true);
    addSkeletons(root);

    try {
      const doc = await fetchPage(root, page);
      // facets.js replaced the results while this page was loading: these items belong to the old filters.
      if (!grid.isConnected) return;
      if (Number(button.dataset.nextPage) !== page) {
        // Another load already appended this page.
        removeSkeletons(root);
        return;
      }
      const { added, finished } = appendPage(root, doc, page);
      if (!finished) setButtonLoading(button, false);
      saveRestoreState(root);

      if (!options.auto && added.length && (hadFocus || finished)) {
        const link = added[0].querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
      }
      if (options.auto) refreshInfinite(root);
    } catch (error) {
      console.error(error);
      removeSkeletons(root);
      if (options.auto) {
        // Keep the shopper on the page: the button stays for a manual retry.
        setButtonLoading(button, false);
        root.dataset.vgAutoLoads = String(AUTO_LOAD_LIMIT);
        return;
      }
      window.location.href = button.getAttribute('href');
    }
  }

  document.addEventListener('click', (event) => {
    const button = closest(event.target, '[data-vg-load-more]');
    if (!button) return;
    const root = getRoot(button);
    if (!root || !('fetch' in window)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return; // open page N in a new tab
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;
    root.dataset.vgAutoLoads = '0';
    loadMore(root, button);
  });

  /* ---------- Infinite scroll (section setting, button stays as the fallback) ---------- */
  function refreshInfinite(root) {
    const state = roots.get(root);
    if (!state) return;
    if (state.infinite) state.infinite.disconnect();
    state.infinite = null;
    const pagination = root.querySelector('[data-vg-pagination][data-vg-infinite]');
    const button = pagination ? pagination.querySelector('[data-vg-load-more]') : null;
    if (!button || !('IntersectionObserver' in window)) return;
    state.infinite = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (button.getAttribute('aria-disabled') === 'true' || !button.isConnected) return;
        const loads = Number(root.dataset.vgAutoLoads) || 0;
        // Every few pages the button takes over again, so the footer stays reachable.
        if (loads >= AUTO_LOAD_LIMIT) return;
        root.dataset.vgAutoLoads = String(loads + 1);
        loadMore(root, button, { auto: true });
      },
      { rootMargin: '0px 0px 640px 0px' }
    );
    state.infinite.observe(button);
  }

  /* ---------- Back-button restore of loaded pages + scroll position ---------- */
  function rememberPosition(root, card) {
    if (!(Number(root.dataset.vgLoadedPage) > startPage(root))) return;
    const extra = { y: Math.round(window.scrollY) };
    if (card) {
      extra.handle = card.dataset.productHandle || '';
      extra.offset = Math.round(card.getBoundingClientRect().top);
    }
    saveRestoreState(root, extra);
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = closest(event.target, 'a[href]');
      const root = link ? getRoot(link) : null;
      if (!root || !closest(link, '[data-vg-grid]')) return;
      rememberPosition(root, closest(link, '[data-product-handle]'));
    },
    true
  );

  window.addEventListener('pagehide', () => {
    roots.forEach((state, root) => {
      if (root.isConnected) rememberPosition(root, null);
    });
  });

  function isBackForward() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry) return entry.type === 'back_forward';
    } catch (error) {
      /* ignore */
    }
    return !!(performance.navigation && performance.navigation.type === 2);
  }

  async function restorePages(root) {
    const key = restoreKey();
    const state = session.get(key);
    if (!state) return;
    if (!isBackForward() || Date.now() - (Number(state.ts) || 0) > RESTORE_TTL) {
      session.remove(key);
      return;
    }
    const pagination = root.querySelector('[data-vg-pagination]');
    const button = pagination ? pagination.querySelector('[data-vg-load-more]') : null;
    const start = startPage(root);
    const pages = Number(pagination ? pagination.dataset.pages : 0) || 0;
    const last = Math.min(Number(state.page) || 0, pages, start + RESTORE_MAX_PAGES);
    if (!button || Number(state.start) !== start || last <= start) return;

    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    root.classList.add('is-restoring');
    setButtonLoading(button, true);
    try {
      const numbers = [];
      for (let page = start + 1; page <= last; page += 1) numbers.push(page);
      const docs = await Promise.all(numbers.map((page) => fetchPage(root, page)));
      let finished = false;
      docs.forEach((doc, index) => {
        if (finished) return;
        finished = appendPage(root, doc, numbers[index]).finished;
      });
      if (!finished) setButtonLoading(button, false);

      const grid = root.querySelector('[data-vg-grid]');
      let target = null;
      if (state.handle && grid && window.CSS && CSS.escape) {
        target = grid.querySelector(`[data-product-handle="${CSS.escape(state.handle)}"]`);
      }
      const top = target
        ? target.getBoundingClientRect().top + window.scrollY - (Number(state.offset) || 0)
        : Number(state.y) || 0;
      window.scrollTo(0, Math.max(0, top));
    } catch (error) {
      console.error(error);
      setButtonLoading(button, false);
    } finally {
      root.classList.remove('is-restoring');
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    }
  }

  /* ---------- Show N results / sheet counts ---------- */
  function updateResultCounts() {
    const source = document.getElementById('ProductCount');
    const count = source ? parseInt(source.dataset.productCount, 10) : NaN;
    if (Number.isFinite(count)) {
      const text = plural(count, 'Show 1 result', `Show ${count} results`);
      document.querySelectorAll('[data-vg-show-results-label]').forEach((label) => {
        if (label.textContent !== text) label.textContent = text;
      });
    }
    window.clearTimeout(loadingTimer);
    document.querySelectorAll('[data-vg-show-results]').forEach((button) => button.classList.remove('is-loading'));
  }

  /* ---------- Recently viewed cards in empty states ---------- */
  async function fillRecent(scope) {
    const strips = Array.from((scope || document).querySelectorAll('[data-vg-recent-strip]:not([data-vg-filled])'));
    if (!strips.length) return;
    const handles = store.list(KEYS.recent);
    strips.forEach((strip) => strip.setAttribute('data-vg-filled', ''));
    if (!handles.length || typeof window.VG.fetchCard !== 'function') return;

    await Promise.all(
      strips.map(async (strip) => {
        const list = strip.querySelector('[data-vg-recent-grid]');
        const limit = Number(strip.dataset.limit) || 4;
        if (!list) return;
        const htmls = await Promise.all(handles.slice(0, limit).map((handle) => window.VG.fetchCard(handle).catch(() => null)));
        const cards = htmls.filter(Boolean);
        if (!cards.length || !strip.isConnected) return;
        for (const html of cards) {
          let content;
          if (typeof window.VG.prepareCard === 'function') {
            content = await window.VG.prepareCard(html, 'recent');
          } else {
            const template = document.createElement('template');
            template.innerHTML = html;
            content = template.content;
          }
          const item = document.createElement('li');
          item.className = 'grid__item';
          item.appendChild(content);
          list.appendChild(item);
        }
        strip.hidden = false;
        syncWishlist(strip);
      })
    );
  }

  /* ---------- Search page: remember successful searches, offer recent ones ---------- */
  function recordSearch() {
    const page = document.querySelector('[data-vg-search-terms]');
    const term = page ? (page.getAttribute('data-vg-search-terms') || '').trim() : '';
    if (!term || term.length > 80) return;
    const lower = term.toLowerCase();
    const list = store.list(KEYS.searches).filter((item) => item.toLowerCase() !== lower);
    list.unshift(term);
    store.set(KEYS.searches, JSON.stringify(list.slice(0, SEARCH_LIMIT)));
  }

  function renderRecentSearches(scope) {
    (scope || document).querySelectorAll('[data-vg-recent-searches]').forEach((box) => {
      const list = box.querySelector('[data-vg-recent-searches-list]');
      if (!list) return;
      const exclude = (box.getAttribute('data-exclude') || '').trim().toLowerCase();
      const url = box.getAttribute('data-search-url') || '/search';
      const terms = store.list(KEYS.searches).filter((term) => term.toLowerCase() !== exclude);
      list.textContent = '';
      terms.slice(0, SEARCH_LIMIT).forEach((term) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'vg-chip vg-chip--recent';
        link.href = `${url}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        link.textContent = term;
        item.appendChild(link);
        list.appendChild(item);
      });
      box.hidden = terms.length === 0;
    });
  }

  document.addEventListener('click', (event) => {
    const clear = closest(event.target, '[data-vg-clear-searches]');
    if (!clear) return;
    event.preventDefault();
    store.remove(KEYS.searches);
    const box = clear.closest('[data-vg-recent-searches]');
    renderRecentSearches(document);
    const focusTarget = box ? box.parentElement && box.parentElement.querySelector('a, input') : null;
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  });

  /* ---------- After facets.js swaps the results ---------- */
  function onResultsUpdated(root) {
    applySizes(root);
    syncSortFromUrl(root);
    mirrorFilterCount(root);
    updateResultCounts();
    const container = root.querySelector('#ProductGridContainer');
    syncWishlist(container);
    root.dataset.vgLoadedPage = String(startPage(root));
    root.dataset.vgAutoLoads = '0';
    refreshInfinite(root);
    fillRecent(container);
    const row = root.querySelector('.vg-quick-filters.is-loading');
    if (row) row.classList.remove('is-loading');
    if (typeof window.VG.reveal === 'function') window.VG.reveal(container);
    document.dispatchEvent(new CustomEvent('vg:collection:updated', { detail: { root } }));
  }

  function init(root) {
    if (!root || roots.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    roots.set(root, { controller, infinite: null });
    root.classList.add('is-enhanced');
    root.dataset.vgLoadedPage = String(startPage(root));
    root.dataset.vgAutoLoads = '0';

    setCols(root, 'd', root.dataset.colsD, false);
    setCols(root, 'm', root.dataset.colsM, false);
    setSidebar(root, root.classList.contains('vg-collection--filters-hidden'), false);
    syncSortFromUrl(root);
    mirrorFilterCount(root);
    initSticky(root, signal);
    refreshInfinite(root);
    fillRecent(root);
    renderRecentSearches(root);
    if (hasFacets()) patchFacets();

    root.addEventListener(
      'click',
      (event) => {
        const desktop = closest(event.target, '[data-vg-cols-d]');
        if (desktop) {
          setCols(root, 'd', desktop.dataset.vgColsD, true);
          flashGrid(root);
          return;
        }
        const mobile = closest(event.target, '[data-vg-cols-m]');
        if (mobile) {
          setCols(root, 'm', mobile.dataset.vgColsM, true);
          flashGrid(root);
          return;
        }
        const sidebar = closest(event.target, '[data-vg-sidebar-toggle]');
        if (sidebar) setSidebar(root, !root.classList.contains('vg-collection--filters-hidden'), true);
      },
      { signal }
    );

    // Lift the toolbar above the sticky header while Dawn's filter drawer is open.
    const toolbar = root.querySelector('[data-vg-toolbar]');
    if (toolbar) {
      toolbar.addEventListener(
        'toggle',
        (event) => {
          if (event.target.classList && event.target.classList.contains('mobile-facets__disclosure')) {
            toolbar.classList.toggle('is-drawer-open', event.target.open);
            document.body.classList.toggle('vg-sheet-open', event.target.open);
          }
        },
        { capture: true, signal }
      );
    }

    const container = root.querySelector('#ProductGridContainer');
    if (container && typeof MutationObserver === 'function') {
      const observer = new MutationObserver(() => onResultsUpdated(root));
      observer.observe(container, { childList: true });
      signal.addEventListener('abort', () => observer.disconnect());
    }

    restorePages(root);
  }

  function destroy(root) {
    const state = roots.get(root);
    if (!state) return;
    state.controller.abort();
    if (state.infinite) state.infinite.disconnect();
    if (openSheet && root.contains(openSheet)) closeSortSheet(false);
    roots.delete(root);
  }

  const initAll = (scope) => {
    const base = scope || document;
    if (base.matches && base.matches('[data-vg-collection]')) init(base);
    base.querySelectorAll('[data-vg-collection]').forEach(init);
  };

  function start() {
    recordSearch();
    initAll();
    renderRecentSearches(document);
    fillRecent(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    renderRecentSearches(event.target);
    fillRecent(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    roots.forEach((state, root) => {
      if (event.target.contains(root)) destroy(root);
    });
  });
})();
